import { useAuth } from '@context/AuthContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useMultisucursalGate } from '@utils/hooks/useMultisucursalGate';
import type { BranchToken } from '@interfaces/general';

export type { BranchToken };

interface UseCanSwitchBranchResult {
  canSwitch: boolean;
  branches: BranchToken[];
  activeBranchName: string;
  hasMultiple: boolean;
  hasPermission: boolean;
  // isRoot: el dueño puede ver el CTA "Crear sucursal" (solicitud a ventas) aunque no
  // pueda cambiar de sucursal todavía. Desacoplado de canSwitch.
  isRoot: boolean;
  switchTo: (branchName: string) => void;
}

export const useCanSwitchBranch = (): UseCanSwitchBranchResult => {
  const { isRoot, refreshLogin } = useAuth();
  const { accountCommerceTokens } = useSubscription();
  const { commerce } = useGetCommerce();

  const branches: BranchToken[] = accountCommerceTokens ?? [];
  const activeBranchName = commerce.nombre ?? '';
  const hasMultiple = branches.length >= 2;
  // Regla de negocio multisucursal (centralizada en useMultisucursalGate): root del comercio con
  // el módulo PEDIDOS_MULTISUCURSAL, O un usuario no-root con ese permiso. El backend
  // (account-detail) aplica un gate L1 sobre accountCommerceTokens; ver OQ-2 en useMultisucursalGate.
  const hasPermission = useMultisucursalGate();
  const canSwitch = hasMultiple && hasPermission;

  const switchTo = (branchName: string) => {
    const target = branches.find((t) => t.nombre === branchName);
    if (target) {
      refreshLogin(target.token, target.id_comercio, {
        nombre: target.nombre,
        logotipo: target.logotipo,
        id_path: target.id_path,
      });
    }
  };

  return {
    canSwitch,
    branches,
    activeBranchName,
    hasMultiple,
    hasPermission,
    isRoot,
    switchTo,
  };
};

export default useCanSwitchBranch;
